import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Checkbox, Stack, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { apiGet } from '../../../generic/Api_Functions'
import MosSmartTable from '../common/MosSmartTable'
import MosSmartDialog from '../common/MosSmartDialog'
import NumericKeypad from '../common/NumericKeypad'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { allowsPartialQuantity, allowsDecimalQuantity } from './instanceGranularity'

const toNumber = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? Math.round(num * 100) / 100 : 0
}
const isSecondUnitSelectable = (factor) => Number.isFinite(Number(factor)) && Number(factor) > 0

// Instance picker for the "Load material" flow. Lists the GENERAL-stock instances (status AVAILABLE)
// of a reference so the operator chooses which instances to load. Granularity from the reference
// flags carried per row (fractionable/unitary) + the per-instance conversionFactor:
//   - whole-only (case 1): checkboxes -> instanceSelections: [{ instanceId }]
//   - partial (cases 2/3/4): a per-instance quantity, entered like the components/consume widget —
//     tap the value to open the NumericKeypad (decimals/commas, integer when it must), with a first-
//     unit/second-unit toggle when the reference is fractionable and the instance has a conversion
//     factor. The quantity is capped at the instance's available and converted back to the first
//     unit on confirm -> instanceSelections: [{ instanceId, quantity }].
// It does NOT load; on accept it hands the instanceSelections back to LoadDialog.
const LoadInstanceSelectionDialog = ({ open, referenceId = 0, referenceCode = '', selectedInstanceSelections = [], onClose, onConfirm }) => {
  const { t_ } = useSmartTranslation()
  const [selectedIds, setSelectedIds] = useState([])
  const [quantityByInstanceId, setQuantityByInstanceId] = useState({})
  const [unitByInstanceId, setUnitByInstanceId] = useState({})
  const [availableById, setAvailableById] = useState({})
  const [factorById, setFactorById] = useState({})
  const [pageInstances, setPageInstances] = useState([])
  const [flags, setFlags] = useState({})
  const [keypadInfo, setKeypadInfo] = useState({ open: false, id: 0, max: 0, val: 0, fractionable: false })

  // Seed from what the caller already had for this reference, only when the dialog opens or the
  // reference changes — not on every parent re-render (which would wipe in-progress edits). Seeded
  // selections are treated as first-unit quantities (that is how they are sent).
  const seedRef = useRef(selectedInstanceSelections)
  seedRef.current = selectedInstanceSelections
  useEffect(() => {
    if (open) {
      const seed = Array.isArray(seedRef.current) ? seedRef.current : []
      setSelectedIds(seed.map((item) => item.instanceId))
      setQuantityByInstanceId(seed.reduce((acc, item) => {
        if (item.quantity !== undefined && item.quantity !== null) acc[item.instanceId] = item.quantity
        return acc
      }, {}))
      setUnitByInstanceId({})
      setKeypadInfo({ open: false, id: 0, max: 0, val: 0, fractionable: false })
    }
  }, [open, referenceId])

  // Paginated (Spring Page). Captures the current page's rows (for "select all") and accumulates the
  // reference flags + per-instance available quantity and conversion factor across pages.
  const fetchInstances = useCallback(async (requestPagination) => {
    if (!referenceId) {
      setPageInstances([])
      return { content: [], totalElements: 0 }
    }
    const result = await apiGet('mos/machineStorage/reference/' + referenceId + '/generalStock/instances', requestPagination)
    const content = Array.isArray(result) ? result : (result?.content ?? [])
    setPageInstances(content)
    if (content.length > 0) {
      setFlags({ fractionable: content[0].fractionable, unitary: content[0].unitary })
    }
    setAvailableById((prev) => {
      const next = { ...prev }
      content.forEach((row) => { next[row.id] = row.quantityAvailable })
      return next
    })
    setFactorById((prev) => {
      const next = { ...prev }
      content.forEach((row) => { next[row.id] = row.conversionFactor })
      return next
    })
    return result
  }, [referenceId])

  const partial = allowsPartialQuantity(flags)
  const referenceFractionable = allowsDecimalQuantity(flags)

  const selectedUnitFor = useCallback((id) => unitByInstanceId[id] ?? 'available', [unitByInstanceId])

  // Whole-instance mode (case 1) --------------------------------------------------------------------
  const toggleInstance = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((current) => current !== id) : [...prev, id]))
  }, [])
  const pageIds = useMemo(() => pageInstances.map((row) => row.id), [pageInstances])
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id))
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const everySelected = pageIds.length > 0 && pageIds.every((id) => prev.includes(id))
      if (everySelected) return prev.filter((id) => !pageIds.includes(id))
      return Array.from(new Set([...prev, ...pageIds]))
    })
  }, [pageIds])

  // Partial mode (cases 2/3/4) ----------------------------------------------------------------------
  // maxFor/decimalsFor mirror the components widget: the max is the instance available in the current
  // unit; decimals are allowed in the first unit when fractionable, and in the second unit unless the
  // reference is unitary.
  const maxFor = useCallback((row) => {
    const factor = row.conversionFactor
    const available = row.quantityAvailable ?? 0
    return selectedUnitFor(row.id) === 'secondUnit' && isSecondUnitSelectable(factor)
      ? toNumber(available * factor)
      : available
  }, [selectedUnitFor])

  const decimalsFor = useCallback((row) => {
    return selectedUnitFor(row.id) === 'secondUnit'
      ? row.unitary !== true
      : row.fractionable === true
  }, [selectedUnitFor])

  const openKeypad = useCallback((row) => {
    const max = maxFor(row)
    if (max <= 0) return
    setKeypadInfo({ open: true, id: row.id, max, val: quantityByInstanceId[row.id] ?? 0, fractionable: decimalsFor(row) })
  }, [maxFor, decimalsFor, quantityByInstanceId])

  const handleKeypadAccept = useCallback((newVal) => {
    if (!keypadInfo.id) return
    let finalVal = Math.min(newVal, keypadInfo.max)
    finalVal = keypadInfo.fractionable ? Math.round(finalVal * 100) / 100 : Math.floor(finalVal)
    finalVal = Math.max(finalVal, 0)
    setQuantityByInstanceId((prev) => ({ ...prev, [keypadInfo.id]: finalVal }))
  }, [keypadInfo])

  // Toggle a row's unit and convert its entered quantity by the factor, re-clamping to the new unit's
  // max (integer when the target is the unitary second unit) — same behaviour as the consume widget.
  const changeUnit = useCallback((row, newUnit) => {
    const id = row.resource ? row.resource.id : row.id
    const factor = row.conversionFactor
    if (!isSecondUnitSelectable(factor)) return
    if (selectedUnitFor(id) === newUnit) return
    setUnitByInstanceId((prev) => ({ ...prev, [id]: newUnit }))
    setQuantityByInstanceId((prev) => {
      const value = prev[id] ?? 0
      if (value === 0) return prev
      const converted = toNumber(newUnit === 'secondUnit' ? value * factor : value / factor)
      const available = row.quantityAvailable ?? 0
      const newMax = newUnit === 'secondUnit' ? toNumber(available * factor) : available
      const clamped = Math.max(0, Math.min(converted, newMax))
      const finalValue = newUnit === 'secondUnit' && row.unitary === true ? Math.floor(clamped) : Math.round(clamped * 100) / 100
      return { ...prev, [id]: finalValue }
    })
  }, [selectedUnitFor])

  const renderUnitCell = useCallback((row, cellUnit) => {
    const factor = row.conversionFactor
    const value = cellUnit === 'secondUnit' ? toNumber((row.quantityAvailable ?? 0) * (factor ?? 0)) : (row.quantityAvailable ?? 0)
    const selectable = referenceFractionable && isSecondUnitSelectable(factor)
    if (!selectable) {
      return <Typography variant='body2'>{value}</Typography>
    }
    const isSelected = selectedUnitFor(row.id) === cellUnit
    return (
      <Box
        data-testid={'load-instance-avail-' + cellUnit + '-' + row.id}
        onClick={() => changeUnit(row, cellUnit)}
        sx={{
          cursor: 'pointer',
          display: 'inline-block',
          minWidth: 48,
          textAlign: 'center',
          px: 1.5,
          py: 0.75,
          borderRadius: 1,
          border: '2px solid',
          borderColor: isSelected ? 'primary.main' : 'transparent',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          fontWeight: isSelected ? 'bold' : 'normal',
          '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' }
        }}
      >
        <Typography variant='body2' sx={{ fontWeight: 'inherit' }}>{value}</Typography>
      </Box>
    )
  }, [referenceFractionable, selectedUnitFor, changeUnit])

  const renderQuantityCell = useCallback((row) => {
    const max = maxFor(row)
    const val = max > 0 ? (quantityByInstanceId[row.id] ?? 0) : 0
    const canEdit = max > 0
    return (
      <Box
        data-testid={'load-instance-qty-' + row.id}
        sx={{
          bgcolor: 'background.default',
          px: 2,
          py: 1,
          borderRadius: 1,
          minWidth: 64,
          textAlign: 'center',
          cursor: canEdit ? 'pointer' : 'default',
          opacity: canEdit ? 1 : 0.38,
          '&:hover': { bgcolor: canEdit ? 'action.hover' : 'background.default' }
        }}
        onClick={() => { if (canEdit) openKeypad(row) }}
      >
        <Typography variant='h6' sx={{ lineHeight: 1 }}>{val}</Typography>
      </Box>
    )
  }, [maxFor, quantityByInstanceId, openKeypad])

  const selectedCount = partial
    ? Object.values(quantityByInstanceId).filter((value) => Number(value) > 0).length
    : selectedIds.length

  const confirmSelection = useCallback(() => {
    let instanceSelections
    if (partial) {
      instanceSelections = Object.entries(quantityByInstanceId)
        .map(([instanceId, value]) => {
          const factor = factorById[instanceId]
          const useSecondUnit = unitByInstanceId[instanceId] === 'secondUnit' && isSecondUnitSelectable(factor)
          let quantity = useSecondUnit ? Number(value) / factor : Number(value)
          quantity = Math.round(quantity * 100) / 100
          const available = availableById[instanceId]
          if (available !== undefined && available !== null) quantity = Math.min(quantity, Number(available))
          return { instanceId: Number(instanceId), quantity }
        })
        .filter((item) => item.quantity > 0)
    } else {
      instanceSelections = selectedIds.map((instanceId) => ({ instanceId }))
    }
    if (onConfirm) onConfirm(instanceSelections)
    if (onClose) onClose()
  }, [partial, quantityByInstanceId, factorById, unitByInstanceId, availableById, selectedIds, onConfirm, onClose])

  const columns = useMemo(() => {
    const infoColumns = [
      { name: t_('Code'), field: 'code', readOnly: true },
      { name: t_('Batch'), field: 'batch', readOnly: true }
    ]
    if (partial) {
      const availabilityColumns = referenceFractionable
        ? [
            { name: t_('Available in general stock'), field: 'quantityAvailable', readOnly: true, search: false, render: (row) => renderUnitCell(row, 'available') },
            { name: t_('Available (Second Unit)'), field: 'availableSecondUnit', readOnly: true, search: false, sortable: false, render: (row) => renderUnitCell(row, 'secondUnit') }
          ]
        : [{ name: t_('Available in general stock'), field: 'quantityAvailable', readOnly: true, search: false }]
      return [
        ...infoColumns,
        ...availabilityColumns,
        { name: t_('Quantity to load'), field: 'quantityToLoad', readOnly: true, search: false, sortable: false, render: renderQuantityCell }
      ]
    }
    return [
      {
        name: '',
        field: 'selection',
        readOnly: true,
        search: false,
        sortable: false,
        render: (row) => (
          <Checkbox
            size='small'
            checked={selectedIds.includes(row.id)}
            onChange={() => toggleInstance(row.id)}
            inputProps={{ 'aria-label': t_('Select instance') }}
          />
        )
      },
      ...infoColumns,
      { name: t_('Available in general stock'), field: 'quantityAvailable', readOnly: true, search: false }
    ]
  }, [partial, referenceFractionable, t_, selectedIds, toggleInstance, renderUnitCell, renderQuantityCell])

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Select instances of {{reference}}', { reference: referenceCode }) })}
      setOpen={open}
      cancelCallback={onClose}
      acceptCallback={confirmSelection}
    >
      <NumericKeypad
        open={keypadInfo.open}
        onClose={() => setKeypadInfo((prev) => ({ ...prev, open: false }))}
        onAccept={handleKeypadAccept}
        initialValue={keypadInfo.val}
        title={t_('Quantity to load')}
        allowDecimals={keypadInfo.fractionable}
      />
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
        {!partial && (
          <>
            <Checkbox
              size='small'
              checked={allPageSelected}
              indeterminate={!allPageSelected && somePageSelected}
              disabled={pageIds.length === 0}
              onChange={toggleSelectAll}
              inputProps={{ 'aria-label': t_('Select all') }}
            />
            <Typography variant='body2' color='text.secondary'>{t_('Select all')}</Typography>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {selectedCount > 0 && (
          <Typography variant='body2' color='text.secondary'>{selectedCount + ' ' + t_('items selected')}</Typography>
        )}
      </Stack>
      <MosSmartTable columns={columns} dataFetch={fetchInstances} pageable tablePageSize={10} showSearchHeader hideExport />
    </MosSmartDialog>
  )
}

export default LoadInstanceSelectionDialog
